import { crsLeadSubmitted } from "@/lib/analytics-events";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState, type FormEvent } from "react";
import { ArrowRight, ExternalLink, Loader2, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  calculateRepayment,
  formatGBP,
  I4F_APR_OPTIONS,
  REPRESENTATIVE_APR,
} from "@/lib/finance-calc";
import { I4F_APPLY_URL, IAR_DISCLAIMER } from "@/content/finance";
import { isValidUkPhone } from "@/lib/phone-validation";
import { sendCalculatorResults } from "@/lib/finance-calculator.functions";

const ZERO_APR_TERMS = [
  { value: 3, label: "3 months (3 months)" },
  { value: 6, label: "6 months (half year)" },
  { value: 12, label: "12 months (1 year)" },
  { value: 24, label: "24 months (2 years)" },
];

const STANDARD_APR_TERMS = [
  { value: 24, label: "24 months (2 years)" },
  { value: 36, label: "36 months (3 years)" },
  { value: 48, label: "48 months (4 years)" },
  { value: 60, label: "60 months (5 years)" },
  { value: 72, label: "72 months (6 years)" },
  { value: 84, label: "84 months (7 years)" },
  { value: 96, label: "96 months (8 years)" },
  { value: 108, label: "108 months (9 years)" },
  { value: 120, label: "120 months (10 years)" },
];

function getTermOptions(aprPercent: number) {
  return aprPercent === 0 ? ZERO_APR_TERMS : STANDARD_APR_TERMS;
}

function fmt(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "£0.00";
  return formatGBP(value);
}

export interface FinanceCalculatorEmbedProps {
  sourcePage?: string;
  idPrefix?: string;
}

export function FinanceCalculatorEmbed({
  sourcePage = "/finance-calculator",
  idPrefix = "calc",
}: FinanceCalculatorEmbedProps) {
  const [price, setPrice] = useState<number | "">("");
  const [deposit, setDeposit] = useState<number | "">("");
  const [termMonths, setTermMonths] = useState<number | "">("");
  const [aprPercent, setAprPercent] = useState<number>(REPRESENTATIVE_APR);

  const priceNum = typeof price === "number" ? price : 0;
  const depositNum = typeof deposit === "number" ? deposit : 0;
  const termNum = typeof termMonths === "number" ? termMonths : 0;

  const hasPrice = priceNum > 0;
  const canCalculate = hasPrice && termNum > 0;

  const calc = useMemo(() => {
    if (!canCalculate) {
      return {
        amount: priceNum,
        deposit: depositNum,
        amountOfCredit: Math.max(0, priceNum - depositNum),
        termMonths: termNum,
        aprPercent,
        monthly: 0,
        total: 0,
        interest: 0,
        financeType:
          aprPercent === 0
            ? ("Interest Free" as const)
            : ("Interest Bearing" as const),
      };
    }
    return calculateRepayment({
      amount: priceNum,
      deposit: depositNum,
      termMonths: termNum,
      aprPercent,
    });
  }, [aprPercent, canCalculate, depositNum, priceNum, termNum]);

  const aprLabel = `${aprPercent.toFixed(2)}% APR`;

  const send = useServerFn(sendCalculatorResults);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  function handleDialogChange(open: boolean) {
    setEmailOpen(open);
    if (!open) {
      // Reset for next open
      setTimeout(() => {
        setStatus("idle");
        setErrors({});
        setFormError(null);
      }, 200);
    }
  }

  async function handleEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setFormError(null);

    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) || "").trim();
    const email = ((fd.get("email") as string) || "").trim();
    const phone = ((fd.get("phone") as string) || "").trim();
    const postcode = ((fd.get("postcode") as string) || "").trim();
    const honeypot = ((fd.get("company_website") as string) || "").trim();

    const localErrors: Record<string, string> = {};
    if (!name) localErrors.name = "Name is required";
    if (!email) localErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      localErrors.email = "Please enter a valid email address";
    if (phone && !isValidUkPhone(phone))
      localErrors.phone = "Please enter a valid UK phone number";
    if (!canCalculate)
      localErrors.form =
        "Please enter a price and select a term above before sending your results.";

    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      if (localErrors.form) setFormError(localErrors.form);
      return;
    }

    setStatus("sending");
    try {
      const res = await send({
        data: {
          name,
          email,
          phone: phone || undefined,
          postcode: postcode || undefined,
          amount: priceNum,
          deposit: depositNum,
          termMonths: termNum,
          aprPercent,
          honeypot,
          source_page: sourcePage,
        },
      });
      if (res.success) {
        if (res.id) crsLeadSubmitted({ leadId: res.id, formId: "finance_calculator", leadType: "finance" });
        setStatus("sent");
      } else {
        setFormError(res.error ?? "Something went wrong. Please try again.");
        setStatus("error");
      }
    } catch (err) {
      console.error(err);
      setFormError("We couldn't send your results right now. Please try again.");
      setStatus("error");
    }
  }

  const labelCls = "block text-sm font-semibold mb-1.5 text-foreground";
  const inputCls =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-ring";
  const errCls = "text-xs text-destructive mt-1";

  return (
    <div>
      <style>{I4F_WIDGET_CSS}</style>

      {/* Ideal4Finance widget (native re-implementation, matching their markup) */}
      <div className="i4f-widget">
        <div className="i4f-card container shadow rounded-3">
          <div className="row gy-3">
            <div className="col-3-md py-2 d-flex justify-content-center">
              <img
                src="https://ideal4finance.com/img/logo-crop.png"
                className="img-fluid w-25 i4f-logo"
                alt="Ideal4Finance"
              />
            </div>
            <div className="col-3-md py-2">
              <div
                className="btn-group d-flex"
                role="group"
                aria-label="APR toggle"
              >
                {I4F_APR_OPTIONS.map((rate) => {
                  const id = `${idPrefix}-btnradio${rate.toFixed(2).replace(".", "")}`;
                  const checked = aprPercent === rate;
                  return (
                    <span key={rate} className="i4f-radio">
                      <input
                        type="radio"
                        className="btn-check"
                        name={`${idPrefix}-btnradio`}
                        id={id}
                        checked={checked}
                        onChange={() => {
                          setAprPercent(rate);
                          const nextTerms = getTermOptions(rate);
                          if (
                            typeof termMonths === "number" &&
                            !nextTerms.some((t) => t.value === termMonths)
                          ) {
                            setTermMonths("");
                          }
                        }}
                        autoComplete="off"
                      />
                      <label
                        className={`btn btn-outline-success${
                          checked ? " active" : ""
                        }`}
                        htmlFor={id}
                      >
                        {rate.toFixed(2)}% APR
                      </label>
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="col-3-md d-flex justify-content-between">
              <p className="mx-3">Finance Type</p>
              <p className="mx-3">{calc.financeType}</p>
            </div>
          </div>

          <div className="js-calc-group">
            <div className="row">
              <div className="col-3-md py-2">
                <input
                  type="number"
                  aria-label="Add price"
                  className="form-control"
                  id={`${idPrefix}-price`}
                  name="price"
                  placeholder="Add price"
                  value={price === "" ? "" : price}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrice(v === "" ? "" : Number(v));
                  }}
                />
              </div>
              <div className="col-3-md py-2">
                <div className="dropdown">
                  <input
                    type="number"
                    aria-label="Enter deposit amount"
                    className="form-control"
                    id={`${idPrefix}-deposit`}
                    name="deposit"
                    placeholder="Enter Deposit Amount"
                    disabled={!hasPrice}
                    value={deposit === "" ? "" : deposit}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDeposit(v === "" ? "" : Number(v));
                    }}
                  />
                </div>
              </div>
              <div className="col-3-md py-2">
                <div className="dropdown">
                  <select
                    className="form-select"
                    id={`${idPrefix}-loan_term`}
                    aria-label="Select loan term"
                    disabled={!hasPrice}
                    value={termMonths === "" ? "" : termMonths}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTermMonths(v === "" ? "" : Number(v));
                    }}
                  >
                    <option value="">Select Loan Term</option>
                    {getTermOptions(aprPercent).map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="row gy-3 py-2 px-3">
            <div className="col-3-md d-flex justify-content-between">
              <p>Amount of credit</p>
              <p>{fmt(calc.amountOfCredit)}</p>
            </div>
            <div className="col-3-md d-flex justify-content-between">
              <p>Monthly</p>
              <p>{fmt(calc.monthly)}</p>
            </div>
            <div className="col-3-md d-flex justify-content-between">
              <p>Total repayable</p>
              <p>{fmt(calc.total)}</p>
            </div>
            <div className="col-3-md d-flex justify-content-between">
              <p>Interest paid</p>
              <p>{fmt(calc.interest)}</p>
            </div>
            <div className="col-3-md d-flex justify-content-between">
              <p>APR</p>
              <p>{aprLabel}</p>
            </div>
          </div>
        </div>

        <div className="row gy-3 py-2 mx-1 i4f-disclaimer">
          <div className="col-3-md text-justify">
            <p>
              <strong>{IAR_DISCLAIMER}</strong>
            </p>
            <p>
              Representative example: <b>{aprLabel}</b>. Based on an amount of credit of{" "}
              <b>{fmt(calc.amountOfCredit)}</b> repayable over{" "}
              <b>{calc.termMonths || 0}</b> months
              {aprPercent === 0 ? (
                <> (Interest Free)</>
              ) : (
                <>
                  {" "}at an interest rate of <b>{aprLabel}</b> pa (fixed)
                </>
              )}
              , with monthly repayment of <b>{fmt(calc.monthly)}</b> and total amount
              payable <b>{fmt(calc.total)}</b>.
            </p>
            <p>
              Please note: the finance calculator provides only a guide and the exact
              amounts will be confirmed during the application process.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-3">
          <a
            href={I4F_APPLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
          >
            Apply With Ideal4Finance
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            disabled={!canCalculate}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Mail className="h-4 w-4" aria-hidden />
            Email Me My Quote
          </button>
          {!canCalculate && (
            <p className="text-xs text-muted-foreground self-center">
              Enter a price and term above to email your quote.
            </p>
          )}
        </div>
      </div>

      {/* Email quote dialog */}
      <Dialog open={emailOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email Me My Quote</DialogTitle>
            <DialogDescription>
              We&apos;ll send a copy of the figures below to your inbox, and a member
              of the team may follow up to answer any questions.
            </DialogDescription>
          </DialogHeader>

          {/* Summary strip */}
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm grid grid-cols-2 gap-y-1.5 gap-x-4">
            <span className="text-muted-foreground">Amount of credit</span>
            <span className="font-semibold text-right">{fmt(calc.amountOfCredit)}</span>
            <span className="text-muted-foreground">Term</span>
            <span className="font-semibold text-right">{calc.termMonths || 0} months</span>
            <span className="text-muted-foreground">Monthly</span>
            <span className="font-semibold text-right">{fmt(calc.monthly)}</span>
            <span className="text-muted-foreground">Total repayable</span>
            <span className="font-semibold text-right">{fmt(calc.total)}</span>
            <span className="text-muted-foreground">APR</span>
            <span className="font-semibold text-right">{aprLabel}</span>
          </div>

          {status === "sent" ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                Thanks. We&apos;ve emailed your calculation. Please check your inbox
                (and spam folder) in the next few minutes.
              </div>
              <DialogFooter>
                <button
                  type="button"
                  onClick={() => handleDialogChange(false)}
                  className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-semibold hover:bg-primary/90 transition-colors"
                >
                  Close
                </button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleEmail} className="space-y-4" noValidate>
              <input
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${idPrefix}-name`} className={labelCls}>
                    Name <span className="text-destructive">*</span>
                  </label>
                  <input id={`${idPrefix}-name`} name="name" required className={inputCls} />
                  {errors.name && <p className={errCls}>{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor={`${idPrefix}-email`} className={labelCls}>
                    Email <span className="text-destructive">*</span>
                  </label>
                  <input
                    id={`${idPrefix}-email`}
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={inputCls}
                  />
                  {errors.email && <p className={errCls}>{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor={`${idPrefix}-phone`} className={labelCls}>
                    Phone (optional)
                  </label>
                  <input
                    id={`${idPrefix}-phone`}
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className={inputCls}
                  />
                  {errors.phone && <p className={errCls}>{errors.phone}</p>}
                </div>
                <div>
                  <label htmlFor={`${idPrefix}-postcode`} className={labelCls}>
                    Postcode (optional)
                  </label>
                  <input
                    id={`${idPrefix}-postcode`}
                    name="postcode"
                    autoComplete="postal-code"
                    className={inputCls}
                  />
                </div>
              </div>

              {formError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full disabled:opacity-60"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Sending...
                  </>
                ) : (
                  <>
                    Send My Quote
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </>
                )}
              </button>

              <p className="text-xs text-muted-foreground leading-relaxed">
                By continuing, you agree to CRS Energy contacting you about your
                enquiry. Finance available subject to status.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Scoped Bootstrap-flavoured styles so the I4F markup renders 1:1 without
 * importing all of Bootstrap globally. Everything is scoped under `.i4f-widget`.
 */
const I4F_WIDGET_CSS = `
.i4f-widget * { box-sizing: border-box; }
.i4f-widget .i4f-card {
  background: #fff;
  border: 1px solid hsl(var(--border));
  padding: 20px 20px 8px;
  border-radius: 1rem !important;
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.08) !important;
}
.i4f-widget .row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin: 0;
  align-items: center;
}
.i4f-widget .row.gy-3 { row-gap: 12px; }
.i4f-widget .py-2 { padding-top: 8px; padding-bottom: 8px; }
.i4f-widget .py-4 { padding-top: 16px; padding-bottom: 16px; }
.i4f-widget .px-3 { padding-left: 12px; padding-right: 12px; }
.i4f-widget .mx-1 { margin-left: 4px; margin-right: 4px; }
.i4f-widget .mx-3 { margin: 0 12px; }
.i4f-widget .mt-4 { margin-top: 1rem; }
.i4f-widget .col-3-md {
  flex: 1 1 220px;
  min-width: 220px;
  margin: 0;
}
.i4f-widget .d-flex { display: flex; }
.i4f-widget .justify-content-between { justify-content: space-between; }
.i4f-widget .justify-content-center { justify-content: center; }
.i4f-widget .img-fluid { max-width: 100%; height: auto; }
.i4f-widget .w-25 { width: 25%; }
.i4f-widget .i4f-logo { max-height: 56px; width: auto; }

/* APR toggle */
.i4f-widget .btn-group { display: flex; gap: 8px; flex-wrap: wrap; }
.i4f-widget .i4f-radio { display: inline-block; }
.i4f-widget .btn-check {
  position: absolute; clip: rect(0,0,0,0); pointer-events: none;
}
.i4f-widget .btn {
  display: inline-block;
  font-weight: 600;
  line-height: 1.4;
  color: hsl(var(--foreground));
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  border: 1px solid transparent;
  padding: .5rem 1rem;
  font-size: .95rem;
  border-radius: .5rem;
  transition: all .15s ease-in-out;
}
.i4f-widget .btn-outline-success {
  color: #198754;
  border-color: #198754;
}
.i4f-widget .btn-outline-success:hover {
  background-color: #19875422;
}
.i4f-widget .btn-check:checked + .btn-outline-success,
.i4f-widget .btn-outline-success.active {
  color: #fff;
  background-color: #198754;
  border-color: #198754;
}

/* Inputs & selects */
.i4f-widget .form-control,
.i4f-widget .form-select {
  display: block;
  width: 100%;
  padding: .5rem .75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: hsl(var(--foreground));
  background-color: #fff;
  border: 1px solid hsl(var(--border));
  border-radius: .5rem;
  transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}
.i4f-widget .form-control:focus,
.i4f-widget .form-select:focus {
  outline: 0;
  border-color: hsl(var(--ring));
  box-shadow: 0 0 0 3px hsl(var(--ring) / .25);
}
.i4f-widget .form-control:disabled,
.i4f-widget .form-select:disabled {
  background-color: #f3f4f6;
  opacity: .7;
  cursor: not-allowed;
}

/* Results & disclaimer typography */
.i4f-widget p { margin: 0; }
.i4f-widget .js-calc-group { margin-top: 8px; }
.i4f-widget .i4f-disclaimer { color: hsl(var(--muted-foreground)); font-size: .875rem; }
.i4f-widget .i4f-disclaimer p { margin-bottom: 10px; line-height: 1.5; }
.i4f-widget .text-justify { text-align: justify; }
.i4f-widget .shadow { box-shadow: 0 .5rem 1rem rgba(0,0,0,.08); }
.i4f-widget .rounded-3 { border-radius: 1rem; }
`;
